/**
 * Resolve a favicon URL for a given site URL. Returns `null` when the input
 * cannot be parsed into a host.
 *
 * We use **DuckDuckGo's icon service** as the primary source because:
 *   - It returns a real, high-resolution favicon when one exists.
 *   - It returns HTTP 404 when the site has no favicon, which surfaces as
 *     a clean `onError` in the browser — no ambiguity.
 *   - It works without CORS and supports both `domain` and `domain.ico`.
 *
 * Google S2 used to be the primary source, but its generic-globe fallback
 * forced a fragile size heuristic (anything ≤16px = "no favicon") that
 * misclassified many legitimate small favicons (e.g. smplify.com, which
 * S2 happens to deliver at native 16×16) as missing.
 *
 * @param input  Anything the user might paste — bare host, full URL, or
 *               protocol-less string ("example.com", "https://example.com/x").
 * @param size   Requested square size hint. DDG ignores this; kept for
 *               cache-key compatibility with code that requested specific
 *               sizes from S2.
 */
// Module-level memo for resolved favicon URLs. We cache at TWO levels:
//
//  1. `urlCache` keyed by `${input}|${size}` — fast-path for repeat calls with
//     the exact same raw input (e.g. re-renders, multiple tiles for the same
//     clip, opening the same editor again).
//
//  2. `hostUrlCache` keyed by `${host}|${size}` — coalesces *different* inputs
//     that resolve to the same host (e.g. "example.com", "https://example.com",
//     "https://example.com/path") to the *same* URL string. This is what
//     keeps the live preview from re-fetching while the user is typing: once
//     the host is known, every subsequent keystroke that still resolves to
//     that host returns the identical URL, so the <img src> doesn't change
//     and the browser doesn't re-request the icon.
//
// Inputs are short URLs and size is bounded by call sites, so unbounded
// growth is not a concern in practice.
const urlCache = new Map<string, string | null>();
const hostUrlCache = new Map<string, string>();

// Per-host memo of "favicon service returned no real favicon" verdicts,
// populated by WebClipFavicon after the first probe (404 / onError or, as a
// safety net, S2's generic 16×16 globe). Lets later renders short-circuit
// straight to the styled fallback.
const genericHostCache = new Map<string, boolean>();

export function getFaviconUrl(input: string, size = 128): string | null {
  const key = `${input}|${size}`;
  const cached = urlCache.get(key);
  if (cached !== undefined) return cached;

  const host = extractHost(input);
  let result: string | null = null;
  if (host) {
    const hostKey = `${host}|${size}`;
    const hostCached = hostUrlCache.get(hostKey);
    if (hostCached !== undefined) {
      result = hostCached;
    } else {
      // DuckDuckGo serves the real favicon when present and 404s otherwise —
      // no generic-globe placeholder, so we don't have to guess.
      result = `https://icons.duckduckgo.com/ip3/${encodeURIComponent(host)}.ico`;
      hostUrlCache.set(hostKey, result);
    }
  }
  urlCache.set(key, result);
  return result;
}

/** Look up whether a given input's host has previously been classified as
 *  "no real favicon" (S2 returned the 16x16 globe). Returns `undefined` when
 *  the host hasn't been probed yet. */
export function getCachedGenericFaviconVerdict(input: string): boolean | undefined {
  const host = extractHost(input);
  if (!host) return undefined;
  return genericHostCache.get(host);
}

/** Record whether the host of `input` resolves to S2's generic globe. */
export function setCachedGenericFaviconVerdict(input: string, isGeneric: boolean): void {
  const host = extractHost(input);
  if (!host) return;
  genericHostCache.set(host, isGeneric);
}

function extractHost(input: string): string | null {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Try as a fully qualified URL first.
  try {
    const u = new URL(trimmed);
    if (u.hostname) return u.hostname;
  } catch {
    // fall through
  }

  // Retry with an implicit protocol so bare hosts ("example.com/path") parse.
  try {
    const u = new URL(`https://${trimmed}`);
    if (u.hostname) return u.hostname;
  } catch {
    return null;
  }

  return null;
}
